import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { Spinner } from '@axioma/ui';

import { api, extractErrorMessage } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

interface VerifyResponse {
  pendingToken: string;
  user: { id: string; email: string; name: string; twoFactorEnabled: boolean };
}

export function MagicLinkVerify(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const setPendingToken = useAuthStore((s) => s.setPendingToken);

  const mutation = useMutation({
    mutationFn: async (rawToken: string) => {
      const { data } = await api.post<VerifyResponse>('/auth/magic-link/verify', {
        token: rawToken,
      });
      return data;
    },
    onSuccess: (data) => {
      setPendingToken(data.pendingToken, data.user);
      navigate(data.user.twoFactorEnabled ? '/auth/2fa/verify' : '/auth/2fa/setup', { replace: true });
    },
  });

  useEffect(() => {
    if (token && !mutation.isPending && !mutation.isSuccess && !mutation.isError) {
      mutation.mutate(token);
    }
  }, [token, mutation]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="ax-hero-bg min-h-screen w-screen flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        {mutation.isError ? (
          <>
            <h1 className="font-display text-2xl text-ax-critical tracking-tight mb-2">
              Link inválido ou expirado
            </h1>
            <p className="ax-body text-ax-text-secondary mb-6">
              {extractErrorMessage(mutation.error)}
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-ax-aurora underline hover:text-ax-aurora-glow font-mono text-sm uppercase tracking-wider"
            >
              Voltar e solicitar novo link
            </button>
          </>
        ) : (
          <>
            <Spinner size="lg" variant="aurora" />
            <p className="ax-body text-ax-text-secondary mt-5">Validando seu acesso...</p>
          </>
        )}
      </div>
    </div>
  );
}
